/**
 * Turn a video link somebody pasted into one that can actually be framed.
 *
 * The address people copy is the address of the page they were watching, and no
 * major video site lets that page be embedded — it refuses to be framed, so the
 * result is a silent blank rectangle. Each site publishes a *different* URL for
 * embedding, and nobody hunts for it before pasting. Verified: a plain request
 * for `bilibili.com/video/BV…` comes back 412, while `player.bilibili.com` is
 * 200.
 *
 * Before this, the host allow-list accepted `bilibili.com`, so a pasted watch
 * link passed every check and rendered an empty frame with no error anywhere —
 * the failure mode that costs the most time, because nothing is reported as
 * wrong.
 *
 * 把别人粘进来的视频链接，转换成真正能被 iframe 嵌入的那个。
 *
 * 人们复制的是自己正在看的那个页面的地址，而主流视频站都不允许这个页面被嵌入
 * ——它拒绝被 frame，于是结果是一个悄无声息的空白矩形。每个站点发布的嵌入地址都*不一样*，
 * 而没有人会在粘贴前去找它。实测：直接请求 `bilibili.com/video/BV…` 返回 412，
 * 而 `player.bilibili.com` 返回 200。
 *
 * 在此之前，域名白名单是接受 `bilibili.com` 的，因此一个粘进来的观看页链接能通过全部检查，
 * 然后渲染出一个空白框，而任何地方都没有报错——这正是最费时间的那种失败：
 * 因为没有任何东西说它错了。
 */

/** Hosts whose player pages may be framed. 播放器页面可被嵌入的域名。 */
const EMBED_HOSTS = ['bilibili.com', 'b23.tv', 'player.bilibili.com', 'youtube.com', 'youtu.be']

/**
 * Parse a URL, or null when it is not one.
 *
 * 解析 URL；不是 URL 则返回 null。
 *
 * @param {string} url Candidate / 待解析。
 * @returns {URL|null} Parsed URL / 解析结果。
 */
function parse(url) {
  try {
    // No base URL on purpose: a relative path throws here and is rejected, which
    // is what we want for an embed source.
    // 刻意不传 base：相对路径会在这里抛错并被拒绝，这正是内嵌源需要的行为。
    const parsed = new URL(String(url || '').trim())
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null
    return parsed
  } catch {
    return null
  }
}

/**
 * True when a URL's host is one we are willing to frame at all.
 *
 * 判断某个链接的域名是否属于我们愿意嵌入的那些。
 *
 * @param {string} url Candidate URL / 待检查链接。
 * @returns {boolean} Whether the host is allow-listed / 域名是否在白名单内。
 */
export function isEmbeddableVideoUrl(url) {
  const parsed = parse(url)
  if (!parsed) return false
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '')
  return EMBED_HOSTS.some((allowed) => host === allowed || host.endsWith(`.${allowed}`))
}

/**
 * The address to put in an iframe, or '' when there is none.
 *
 * Returning '' rather than the original is the point: the caller then shows the
 * link instead of a frame, and a visitor gets something they can click rather
 * than a blank rectangle. A `b23.tv` short link takes this path — resolving one
 * needs a request to bilibili, which a rendering function has no business
 * making.
 *
 * 放进 iframe 的地址；没有则返回空串。
 *
 * 返回空串而不是原地址，正是关键：调用方于是显示链接而不是画一个框，
 * 访客得到的是一个能点的东西，而不是一块空白。`b23.tv` 短链走的就是这条路
 * ——要解析它得向 B 站发一次请求，而一个渲染函数没有理由去发请求。
 *
 * @param {string} url Pasted URL / 粘贴进来的链接。
 * @returns {string} Embeddable URL, or '' / 可嵌入地址；没有则空串。
 */
export function toEmbedUrl(url) {
  const parsed = parse(url)
  if (!parsed) return ''
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '')

  if (host === 'player.bilibili.com') return parsed.toString()

  if (host === 'bilibili.com' || host.endsWith('.bilibili.com')) {
    const match = /\/video\/(BV[0-9A-Za-z]+|av\d+)/i.exec(parsed.pathname)
    if (!match) return ''
    const id = match[1]
    const player = new URL('https://player.bilibili.com/player.html')
    if (id.toLowerCase().startsWith('av')) player.searchParams.set('aid', id.slice(2))
    else player.searchParams.set('bvid', id)
    // A multi-part upload carries its part number in `?p=`, and dropping it
    // would quietly play part one of a lecture series whatever was linked.
    // 多 P 投稿的分集号在 `?p=` 里，丢掉它会让一个系列讲座无论链的是哪一集，
    // 都不声不响地播成第一集。
    const part = parsed.searchParams.get('p')
    if (part) player.searchParams.set('p', part)
    player.searchParams.set('autoplay', '0')
    return player.toString()
  }

  if (host === 'youtu.be') {
    const id = parsed.pathname.replace(/^\/+/, '')
    return id ? `https://www.youtube.com/embed/${encodeURIComponent(id)}` : ''
  }

  if (host === 'youtube.com' || host.endsWith('.youtube.com')) {
    if (parsed.pathname.startsWith('/embed/')) return parsed.toString()
    const id = parsed.searchParams.get('v')
    return id ? `https://www.youtube.com/embed/${encodeURIComponent(id)}` : ''
  }

  return ''
}
